import { Grid, Header } from "@amzn/awsui-components-react";
import CustomTextFormField from "common/component/CustomFormField/CustomTextFormField";
import { getString } from "common/ui_string_labels/ui-strings-utils";
import { CARD_HEADER_INPUT_TEST_ID, CARD_NAME_TEST_ID, CARD_SPECIFICATION_TEST_ID } from "helpers";
import { CatalogItem } from "interfaces";
import { JSX, useEffect, useMemo, useState } from "react";

/**
 * The maximum size allowed for the quantity
 */
const MAX_QUANTITY_SIZE = 2 ** 30;

/**
 * Represent the props for the CardHeader component
 * @interface
 * @property {string} name - The name of the card item
 * @property {string} description - The description of the card item
 * @property {number} value - The current value or quantity of the card item
 * @property {(value: number) => void} setValue - A callback function that is called when the value of the card item changes
 * @property {React.ReactNode} [quantityErrorText] - An optional React node representing an error message for the quantity input
 * @property {string} toucpoint = The touchpoint for the UxM Component
 */
export interface CardHeaderProps {
    name: string;
    description: string;
    value: number;
    setValue: (event: number) => void;
    quantityErrorText?: React.ReactNode;
    touchpoint: string;
}

/**
 * Renders a header component for a card with a quantity input field
 *
 * @param {string} name - The name to display in the header
 * @param {string} description - The description text to show below the name
 * @param {number} value - The current quantity value
 * @param {(value: number) => void} setValue - Callback function to update the quantity value
 * @property {React.ReactNode} [quantityErrorText] - An optional React node representing an error message for the quantity input
 *
 * @returns {JSX.Element} A grid containing the header and quantity input
 */
export const CardHeaderWithQuantityInput = ({
    name,
    description,
    value,
    setValue,
    quantityErrorText,
    touchpoint,
}: CardHeaderProps): JSX.Element => {
    function onChangeValue(value: string) {
        const newValue = parseInt(value);
        if (newValue < 0) {
            setValue(0);
        } else {
            setValue(newValue);
        }
    }

    useEffect(() => {
        if (value > MAX_QUANTITY_SIZE) {
            setValue(MAX_QUANTITY_SIZE);
        }
    }, [value]);

    return (
        <Grid gridDefinition={[{ colspan: 9 }, { colspan: 3 }]}>
            <Header
                variant="h3"
                description={<span data-testid={CARD_SPECIFICATION_TEST_ID}>{description}</span>}
                data-testid={CARD_NAME_TEST_ID}
            >
                {name}
            </Header>
            <CustomTextFormField
                data-testid={CARD_HEADER_INPUT_TEST_ID}
                label={getString("common.component.LocalITHardwareRestockContent.card.quantityInput")}
                value={value.toString()}
                setValue={onChangeValue}
                errorText={quantityErrorText}
                type={"number"}
                touchpoint={touchpoint}
            />
        </Grid>
    );
};

/**
 * Represent the props for the useCardTable hook
 *
 * @interface
 * @property {(params: { quantities: Map<number, number> }) => void} onSubmitNewOrder - Callback function to handle new order submission
 */
export interface useCardTableProps {
    onSubmitNewOrder: (params: { quantities: Map<CatalogItem, number> }) => void;
    quantities?: Map<CatalogItem, number>;
}

/**
 * Represent the output of the useCardTable hook
 *
 * @interface
 * @property {Map<CatalogRequestResponse, number>} quantities - A map of catalog items to their quantity
 * @property {(item: CatalogRequestResponse) => number} getQuantity - Function to get the quantity of a card
 * @property {(item: CatalogRequestResponse, value: number) => void} updateQuantity - Function to update the quantity of a card
 * @property {() => void} onSubmitNewOrderHandler - Function to handle new order submission
 */
export interface useCardTableOutput {
    quantities: Map<CatalogItem, number>;
    getQuantity: (item: CatalogItem) => number;
    updateQuantity: (item: CatalogItem, value: number) => void;
    onSubmitNewOrderHandler: () => void;
}

/**
 * Custom hook for managing the card table component
 *
 * @param {(params: { quantities: Map<number, number> }) => void} onSubmitNewOrder - Callback function to handle new order submission
 * @returns {useCardTableOutput} An object containing the quantities, getQuantity, updateQuantity, and onSubmitNewOrderHandler
 */
export function useCardTable({
    onSubmitNewOrder,
    quantities: receivedQuantities,
}: useCardTableProps): useCardTableOutput {
    const [quantities, setQuantities] = useState<Map<string, number>>(new Map());
    const [mappedItems, setMappedItems] = useState<Map<string, CatalogItem>>(new Map());

    useEffect(() => {
        if (receivedQuantities === undefined || receivedQuantities.size === 0) {
            return;
        }

        const newQuantities = new Map<string, number>();
        const newMappedItems = new Map<string, CatalogItem>();

        receivedQuantities.forEach((quantity, item) => {
            const taxonomyId = item.taxonomyId;
            newQuantities.set(taxonomyId, quantity);
            newMappedItems.set(taxonomyId, item);
        });

        setQuantities(newQuantities);
        setMappedItems(newMappedItems);
    }, [receivedQuantities]);

    const getQuantity = (item: CatalogItem) => quantities.get(item.taxonomyId) || 0;

    const removeQuantity = (item: CatalogItem) => {
        const taxonomyId = item.taxonomyId;
        const newQuantities = new Map(quantities);
        const newMappedItems = new Map(mappedItems);

        newQuantities.delete(taxonomyId);
        newMappedItems.delete(taxonomyId);

        setQuantities(newQuantities);
        setMappedItems(newMappedItems);
    };

    const updateQuantity = (item: CatalogItem, value: number) => {
        if (value <= 0 || !value) {
            removeQuantity(item);
            return;
        }

        const taxonomyId = item.taxonomyId;
        setQuantities((prev) => new Map(prev).set(taxonomyId, value));
        setMappedItems((prev) => new Map(prev).set(taxonomyId, item));
    };

    const onSubmitNewOrderHandler = () => {
        const itemQuantities = new Map<CatalogItem, number>();
        quantities.forEach((quantity, taxonomyId) => {
            const item = mappedItems.get(taxonomyId);
            if (item) {
                itemQuantities.set(item, quantity);
            }
        });
        onSubmitNewOrder({ quantities: itemQuantities });
    };

    const itemQuantities = useMemo(() => {
        const result = new Map<CatalogItem, number>();
        quantities.forEach((quantity, taxonomyId) => {
            const item = mappedItems.get(taxonomyId);
            if (item) {
                result.set(item, quantity);
            }
        });
        return result;
    }, [quantities, mappedItems]);

    return {
        quantities: itemQuantities,
        getQuantity,
        updateQuantity,
        onSubmitNewOrderHandler,
    };
}
